import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { ApiError, login } from '../lib/api-client';
import { storeAccessToken } from '../lib/auth';
import { useI18n } from '../lib/i18n-context';

interface LocationState {
  from?: string;
}

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await login({ email, password });
      if (res.access_token === undefined) {
        // 2FA-required step 1：本 scaffold 未做 totp 表單；提示使用者
        setError(t('login.err2fa'));
        setSubmitting(false);
        return;
      }
      storeAccessToken(res.access_token);
      const state = location.state as LocationState | null;
      const target = state?.from ?? '/';
      navigate(target, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          err.status === 401 ? t('login.errBadCredentials') : (err.problem?.detail ?? err.message),
        );
      } else {
        setError(t('login.errGeneric'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="app-shell">
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <LanguageSwitcher />
      </div>
      <div style={{ textAlign: 'center', margin: '12px 0 28px' }}>
        <AnchorMark />
        <div style={{ fontSize: 22, fontWeight: 700, marginTop: 6 }}>{t('login.brand')}</div>
      </div>
      <h1>{t('login.title')}</h1>
      <form onSubmit={handleSubmit} noValidate>
        <div className="form-field">
          <label htmlFor="email">{t('login.email')}</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            placeholder={t('login.emailPlaceholder')}
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="password">{t('login.password')}</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {error !== null && <div className="error-text">{error}</div>}
        <button type="submit" className="btn-primary btn-block" disabled={submitting}>
          {submitting ? t('login.submitting') : t('login.submit')}
        </button>
      </form>
    </div>
  );
}

/** 品牌錨標誌（對齊 .pen 設計稿之海港藍線條錨）。 */
function AnchorMark(): JSX.Element {
  return (
    <svg
      width="52"
      height="52"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#0e5a8c"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="5" r="3" />
      <line x1="12" y1="22" x2="12" y2="8" />
      <path d="M5 12H2a10 10 0 0 0 20 0h-3" />
    </svg>
  );
}
